import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, I18nextProvider } from 'react-i18next';
import i18n from '../i18n';
import { CustomThemeProvider, useThemeMode } from '../ThemeContext';

// MUI & Icon Imports
import { Box, Paper, Button, TextField, Alert, CircularProgress, IconButton, Grid, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Card, CardHeader, CardContent, CssBaseline, Drawer, Toolbar, Typography, AppBar, ListItemButton, Avatar } from '@mui/material';
import { Settings as SettingsIcon, Storefront as StorefrontIcon, Timer as TimerIcon, DesignServices as DesignServicesIcon, Gavel as GavelIcon, CheckCircleOutline as CheckCircleOutlineIcon, Block as BlockIcon, Timelapse as TimelapseIcon, EventNote as EventNoteIcon, PowerOff as PowerOffIcon, Dashboard as DashboardIcon, Science as ScienceIcon, AccountBox as AccountBoxIcon, Brightness4 as Brightness4Icon, Brightness7 as Brightness7Icon, Menu as MenuIcon, Person as PersonIcon, SmartToy as SmartToyIcon, Send as SendIcon, Email as EmailIcon, VpnKey as VpnKeyIcon, Cached as CachedIcon, PlayCircleOutline as PlayCircleOutlineIcon } from '@mui/icons-material';

// --- MOCK DATA ---
const mockConfigData = {
  business_name: "Flavius Barbershop Cool",
  timezone_str: "UTC+3",
  operating_hours: { Monday: "09:00-18:00", Tuesday: "09:00-18:00", Wednesday: "09:00-18:00", Thursday: "09:00-18:00", Friday: "09:00-17:00", Saturday: "10:00-15:00", Sunday: "Closed" },
  services: [{ name: "Standard Haircut", duration_minutes: 34, description: "A classic haircut." }, { name: "Beard Trim & Style", duration_minutes: 20, description: "Keep your beard looking sharp." }],
  booking_rules: { min_advance_hours: 2, max_advance_days: 7, allow_only_listed_services: true, max_active_appointments_per_user: 1 }
};
const mockUser = { username: 'flavius', tenant_id: 'Flavius', is_superadmin: false, email: 'flavius@flavy.us' };

// --- DEMO PAGE COMPONENTS (No changes in these, except for the fix in DemoDashboard) ---
function DemoDashboard() {
    const { t } = useTranslation();
    const config = mockConfigData;
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" component="h1">{t('welcomeUser', {name: mockUser.username})}</Typography>
                    <Typography color="text.secondary">{t('tenantIdDisplay', {tenantId: mockUser.tenant_id})}</Typography>
                </Box>
                <Button variant="contained" color="primary" startIcon={<SettingsIcon />}>{t('editConfiguration')}</Button>
            </Box>
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardHeader avatar={<StorefrontIcon color="primary" />} title={t('businessInformationSection')} titleTypographyProps={{variant: 'h6'}}/>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">{t('businessName')}</Typography>
                            <Typography variant="body1">{config.business_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">{t('timezoneLabel')}</Typography>
                            <Typography variant="body1">{config.timezone_str}</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardHeader avatar={<TimerIcon color="primary" />} title={t('operatingHoursSection')} titleTypographyProps={{variant: 'h6'}}/>
                <List sx={{p: 0}}>
                    {Object.entries(config.operating_hours).map(([day, hours]) => (
                        <ListItem key={day} divider>
                            <ListItemIcon sx={{minWidth: 'auto', mr: 2}}><EventNoteIcon /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="subtitle1" fontWeight="medium">{t(day.toLowerCase(), day)}</Typography>}
                                secondary={
                                    hours === "Closed"
                                        ? <Chip label={t('closed')} size="small" icon={<BlockIcon sx={{fontSize: '1rem'}}/>} />
                                        : <Typography variant="body2" color="text.secondary">{hours}</Typography>
                                }
                                disableTypography // <-- FIX: Prevent ListItemText from wrapping content in Typography
                            />
                        </ListItem>
                    ))}
                </List>
            </Card>
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardHeader avatar={<DesignServicesIcon color="primary" />} title={t('servicesOfferedSection')} titleTypographyProps={{variant: 'h6'}}/>
                <CardContent>
                    {config.services.map((service, index) => (
                        <Box key={index}>
                            <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
                                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>{service.name}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb:1}}>{service.description}</Typography>
                                <Chip icon={<TimelapseIcon sx={{fontSize: '1rem'}}/>} label={t('durationMinutes', { duration: service.duration_minutes })} size="small" variant="outlined"/>
                            </Paper>
                            {index < config.services.length - 1 && <Divider />}
                        </Box>
                    ))}
                </CardContent>
            </Card>
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardHeader avatar={<GavelIcon color="primary" />} title={t('bookingRulesSection')} titleTypographyProps={{variant: 'h6'}}/>
                <CardContent>
                    <Grid container spacing={2.5} sx={{px: 1}}>
                        {[{ labelKey: "minAdvanceBookingLabel", value: t('hoursUnit', {count: config.booking_rules.min_advance_hours}) }, { labelKey: "maxAdvanceBookingLabel", value: t('daysUnit', {count: config.booking_rules.max_advance_days}) }, { labelKey: "maxActiveAppointmentsLabel", value: config.booking_rules.max_active_appointments_per_user }, { labelKey: "allowOnlyListedServicesLabel", valueIsChip: true, chipLabel: config.booking_rules.allow_only_listed_services ? t('yes') : t('no'), chipColor: config.booking_rules.allow_only_listed_services ? "success" : "error", chipIcon: config.booking_rules.allow_only_listed_services ? <CheckCircleOutlineIcon/> : <BlockIcon/> },].map((rule, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                    <Typography variant="subtitle2" color="text.secondary">{t(rule.labelKey)}</Typography>
                                    {rule.valueIsChip ? (
                                        <Chip icon={rule.chipIcon} label={rule.chipLabel} color={rule.chipColor} size="small" sx={{mt: 0.5, alignSelf: 'flex-start'}} />
                                    ) : (
                                        <Typography variant="body1">{rule.value}</Typography>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Paper elevation={3} sx={{p: {xs: 2, sm: 3}, backgroundColor: '#3c3227'}}>
                <Typography variant="h6" gutterBottom color="white">{t('whatsapp.connectionTitle')}</Typography>
                <Alert severity="warning" icon={<PowerOffIcon fontSize="inherit" />} sx={{ '& .MuiAlert-message': { width: '100%' }, backgroundColor: '#574836', color: '#ffb74d' }}>
                    <Typography fontWeight="bold">Deconectat</Typography>
                    <Typography variant="body2">{t('whatsapp.connectedTo')}</Typography>
                </Alert>
                <Box sx={{mt: 2, display: 'flex', gap: 1}}>
                    <Button variant='contained' color='warning' startIcon={<PlayCircleOutlineIcon />}>Pornește Bot</Button>
                    <Button variant='contained' color='warning' startIcon={<CachedIcon />}>Reconectare</Button>
                </Box>
            </Paper>
        </Box>
    );
}

function DemoTestAI() {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), [conversation]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!userInput.trim() || isLoading) return;

    const userMessage = { sender: 'user', text: userInput };
    setConversation(prev => [...prev, userMessage]);
    setUserInput('');
    setIsLoading(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = { sender: 'ai', text: `This is a simulated response to: "${userMessage.text}". In the real app, I would provide a helpful answer.` };
      setConversation(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1500);
  };

  return (
    <Paper elevation={3} sx={{ p: {xs:1, sm:2, md:3}, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', maxHeight: '700px' }}>
      <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 2, display:'flex', alignItems:'center', justifyContent:'center'}}><SmartToyIcon sx={{mr:1}} color="primary"/> {t('testAITitle')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{textAlign: 'center', mb:2}} dangerouslySetInnerHTML={{ __html: t('testAIChatSubtitle', { tenantId: mockUser.tenant_id }) }} />
      <Divider sx={{mb:2}}/>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, pr: 1 }}>
        <List>
          {conversation.map((msg, index) => (
            <ListItem key={index} sx={{ py: 1.5, flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row' }}>
              <Avatar sx={{ bgcolor: msg.sender === 'user' ? 'secondary.main' : 'primary.main', ml: msg.sender === 'user' ? 2 : 0, mr: msg.sender === 'ai' ? 2 : 0 }}>
                {msg.sender === 'user' ? <PersonIcon /> : <SmartToyIcon />}
              </Avatar>
              <Paper elevation={1} sx={{ p: 1.5, borderRadius: msg.sender === 'user' ? '20px 20px 5px 20px' : '5px 20px 20px 20px', bgcolor: msg.sender === 'user' ? 'secondary.dark' : 'primary.dark', maxWidth: '75%' }}>
                <ListItemText primary={msg.text} primaryTypographyProps={{variant:'body1'}}/>
              </Paper>
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>
      <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', alignItems: 'center', mt: 'auto', pt:1, borderTop: 1, borderColor: 'divider' }}>
        <TextField fullWidth variant="outlined" placeholder={t('testAIMessagePlaceholder')} value={userInput} onChange={(e) => setUserInput(e.target.value)} disabled={isLoading} sx={{mr:1}} />
        <Button type="submit" variant="contained" color="primary" disabled={isLoading || !userInput.trim()} sx={{height: '56px'}}>
          {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
        </Button>
      </Box>
    </Paper>
  );
}

function DemoProfile() {
    const { t } = useTranslation();
    const [email, setEmail] = useState(mockUser.email);
    const [isUpdating, setIsUpdating] = useState(false);
    const [success, setSuccess] = useState('');

    const handleUpdate = (e) => {
        e.preventDefault();
        setIsUpdating(true);
        // Simulate API call
        setTimeout(() => {
            setSuccess('Profile updated successfully! (Demo)');
            setIsUpdating(false);
            setTimeout(() => setSuccess(''), 4000); // Clear message after 4 seconds
        }, 1000);
    };

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, maxWidth: '800px', margin: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountBoxIcon color="primary" sx={{ fontSize: '2.5rem', mr: 1.5 }} />
                <Typography variant="h5">{t('adminProfileTitle')}</Typography>
            </Box>
            <Grid container spacing={5}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ mr: 1.5 }} /> Update Email
                    </Typography>
                    <Box component="form" onSubmit={handleUpdate}>
                        <TextField margin="normal" required fullWidth label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={isUpdating} />
                        <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={isUpdating}>
                            {isUpdating ? <CircularProgress size={24} /> : "Update Email"}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <VpnKeyIcon sx={{ mr: 1.5 }} /> Change Password
                    </Typography>
                    <Box component="form" onSubmit={handleUpdate}>
                        <TextField margin="normal" required fullWidth label="Current Password" type="password" disabled={isUpdating} />
                        <TextField margin="normal" required fullWidth label="New Password" type="password" disabled={isUpdating} />
                        <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={isUpdating}>
                            {isUpdating ? <CircularProgress size={24} /> : "Change Password"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}
        </Paper>
    );
}

// --- LAYOUT COMPONENTS ---
const DRAWER_WIDTH = 240;

function LanguageSwitcher() {
    const { i18n } = useTranslation();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, border: '1px solid', borderColor:'divider', borderRadius: '4px' }}>
            <Button size="small" onClick={() => i18n.changeLanguage('en')} disabled={i18n.language.startsWith('en')} sx={{ color: 'text.secondary', bgcolor: i18n.language.startsWith('en') ? 'action.selected' : 'transparent'}}>EN</Button>
            <Divider orientation="vertical" flexItem />
            <Button size="small" onClick={() => i18n.changeLanguage('ro')} disabled={i18n.language.startsWith('ro')} sx={{ color: 'text.secondary', bgcolor: i18n.language.startsWith('ro') ? 'action.selected' : 'transparent'}}>RO</Button>
        </Box>
    );
}

function MockMainLayout({ activeView, setActiveView }) { // <-- RECEIVES PROPS
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { mode, toggleThemeMode } = useThemeMode();
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // This function decides which component to render based on state
  const renderActiveView = () => {
    switch (activeView) {
      case 'test-ai': return <DemoTestAI />;
      case 'profile': return <DemoProfile />;
      case 'dashboard':
      default:
        return <DemoDashboard />;
    }
  };

  const navItems = [
    { id: 'dashboard', text: t('dashboardNav'), icon: <DashboardIcon /> },
    { id: 'test-ai', text: t('testMyAIAssistant'), icon: <ScienceIcon /> },
    { id: 'profile', text: t('myProfile'), icon: <AccountBoxIcon /> },
  ];

  const drawerContent = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <Typography variant="h5" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          {t('sidebar.tenantTitle')}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navItems.map((item) => {
          const isActive = activeView === item.id;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => { setActiveView(item.id); handleDrawerToggle(); }} // <-- USE ONCLICK
                sx={{ // <-- USE STATE FOR STYLING
                  backgroundColor: isActive ? 'action.selected' : 'transparent',
                  borderRight: isActive ? '3px solid' : 'none',
                  borderColor: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: isActive ? 'primary.main' : 'inherit',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2 }}>
        <Divider sx={{ mb: 2 }}/>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <LanguageSwitcher />
            <IconButton onClick={toggleThemeMode} color="inherit" title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
        </Box>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { sm: `${DRAWER_WIDTH}px` }, backgroundColor: 'background.paper', color: 'text.primary', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
                <MenuIcon />
            </IconButton>
            {/* Removed the fixed "Dashboard" text from AppBar and made it dynamic based on active view */}
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }} color="text.primary">
                {navItems.find(item => item.id === activeView)?.text || t('dashboardNav')}
            </Typography>
            <IconButton size="large" color="inherit">
                <AccountBoxIcon />
            </IconButton>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH } }}>
            {drawerContent}
        </Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' } }} open>
            {drawerContent}
        </Drawer>
      </Box>
      {/* FIX: Removed ml (margin-left) from the main content box */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: '64px', overflow: 'auto', height: 'calc(100vh - 64px)' }}>
        {renderActiveView()} {/* <-- RENDER VIEW BASED ON STATE */}
      </Box>
    </Box>
  );
}

// --- FINAL EXPORTED COMPONENT ---
function ScheduailerDemo() {
  const [activeView, setActiveView] = useState('dashboard'); // <-- STATE LIVES HERE

  useEffect(() => {
    i18n.changeLanguage('ro');
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <CustomThemeProvider>
        {/* No Router here! Just the layout component */}
        <MockMainLayout activeView={activeView} setActiveView={setActiveView} />
      </CustomThemeProvider>
    </I18nextProvider>
  );
}

export default ScheduailerDemo;